import { Button, ErrorBanner, SuccessBanner, cn } from "@fmc-digital/ui";
import { useTranslation } from "@fmc-digital/i18n";
import { LoginHeader } from "../molecules/LoginHeader";
import { LoginFields } from "../molecules/LoginFields";
import { loginFormStyles, loginFormContentStyles, loginFormErrorStyles } from "../../pages/login-form.styles";
import { trackForgotEmailEvent, ForgotEmailEvent, trackForgotPasswordEvent, ForgotPasswordEvent, trackLoginEvent, LoginEvent } from "@/utils/analytics.utils";
import { useEffect, useState, useRef } from "react";
import { useScrollIntoViewIfNotVisible } from "../../hooks/useScrollIntoViewIfNotVisible";
import { AcpUnauthorizedBanner } from "../molecules/AcpUnauthorizedBanner";
import { LocalTimeErrorBanner } from "../molecules/LocalTimeErrorBanner/LocalTimeErrorBanner";

export interface LoginContainerProps {
  readonly username: string;
  readonly password: string;
  readonly showPassword: boolean;
  readonly setUsername: (v: string) => void;
  readonly setPassword: (v: string) => void;
  readonly setShowPassword: (v: boolean) => void;
  readonly authLoading: boolean;
  readonly error: any;
  readonly onSubmit: (e: React.SubmitEvent<HTMLFormElement>) => void;
  readonly navigate: (path: string) => void;
  readonly getErrorMessage: () => string;
  readonly showEmailRecoveredSuccess: boolean;
  readonly showPasswordResetSuccess: boolean;
  readonly emailError?: string | null;
  readonly passwordError?: string | null;
  readonly isLoginDisabled: boolean;
  /** Register CTA — parent applies VWO + `registerNowUrl` vs in-app route. */
  readonly onRegisterClick: () => void;
  readonly showUnauthorizedAcpBanner?: boolean;
  /** Device clock does not match the server clock (utils/clockSync.ts). Figma node 6457:22443. */
  readonly showLocalTimeError?: boolean;
}

export function LoginContainer({
  username,
  password,
  showPassword,
  setUsername,
  setPassword,
  setShowPassword,
  authLoading,
  error,
  onSubmit,
  navigate,
  getErrorMessage,
  showEmailRecoveredSuccess,
  showPasswordResetSuccess,
  emailError,
  passwordError,
  isLoginDisabled,
  onRegisterClick,
  showUnauthorizedAcpBanner,
  showLocalTimeError,
}: LoginContainerProps) {
  const { t } = useTranslation();

  // Session timeout alert logic (cookie-based, in the right place)
  const [showSessionTimeoutAlert, setShowSessionTimeoutAlert] = useState(false);
  const sessionTimeoutRef = useRef<HTMLDivElement>(null);
  const emailRecoveredRef = useRef<HTMLDivElement>(null);
  const passwordResetRef = useRef<HTMLDivElement>(null);
  const unauthorizedAcpRef = useRef<HTMLDivElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const localTimeErrorRef = useRef<HTMLDivElement>(null);
  const localTimeBannerVisible = !!showLocalTimeError && !error;

  useEffect(() => {
    if (!localTimeBannerVisible) return;
    trackLoginEvent(LoginEvent.LOCAL_TIME_ERROR);
  }, [localTimeBannerVisible]);

  useEffect(() => {
    if (typeof document !== "undefined") {
      const cookies = document.cookie.split(";").map((c) => c.trim());
      const found = cookies.find((c) => c.startsWith("sessiontimedout="));

      if (found && found.split("=")[1] === "true") {
        setShowSessionTimeoutAlert(true);
      }
    }
  }, []);

  // Use custom hook for scroll-into-view for all banners
  useScrollIntoViewIfNotVisible(sessionTimeoutRef, showSessionTimeoutAlert && !error);
  useScrollIntoViewIfNotVisible(emailRecoveredRef, showEmailRecoveredSuccess && !error);
  useScrollIntoViewIfNotVisible(passwordResetRef, showPasswordResetSuccess && !error);
  useScrollIntoViewIfNotVisible(unauthorizedAcpRef, !!showUnauthorizedAcpBanner && !error);
  useScrollIntoViewIfNotVisible(errorRef, !!error);
  useScrollIntoViewIfNotVisible(localTimeErrorRef, localTimeBannerVisible);

  // Clear the sessiontimedout cookie as soon as the alert is visible
  useEffect(() => {
    if (showSessionTimeoutAlert && typeof document !== 'undefined') {
      document.cookie = 'sessiontimedout=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    }
  }, [showSessionTimeoutAlert]);

  return (
    <div className={cn(loginFormStyles())} data-testid="login-page">
      <LoginHeader 
        title={t("auth.login.title")} 
        description={t("auth.login.subtitle")} 
        testId="login-header" 
      />
      {localTimeBannerVisible && (
        <div
          ref={localTimeErrorRef}
          tabIndex={-1}
          role="alert"
          className="mb-4 w-full md:mx-auto md:mb-0 md:max-w-[396px] lg:max-w-[476px]"
          data-testid="local-time-error"
        >
          <LocalTimeErrorBanner />
        </div>
      )}
      <div className={cn(loginFormContentStyles())} data-testid="login-card">
        <LoginFields
          username={username}
          password={password}
          showPassword={showPassword}
          onUsernameChange={setUsername}
          onPasswordChange={setPassword}
          onTogglePasswordVisibility={() => setShowPassword(!showPassword)}
          onSubmit={onSubmit}
          disabled={authLoading}
          emailError={emailError}
          passwordError={passwordError}
        >
          <Button
            type="submit"
            variant="filled"
            fullWidth
            loading={authLoading}
            disabled={!username || !password || isLoginDisabled || !!showLocalTimeError}
            testID="login-button"
            className="!mt-5 md:!mt-8"
          >
            {"Log in"}
          </Button>
        </LoginFields>
        <div className="mt-4 flex flex-col items-center">
          <div className="flex justify-center items-center gap-[18px]">
            <Button
              variant="text"
              className="auth-action-button"
              onClick={() => {
                trackForgotEmailEvent(ForgotEmailEvent.CLICKED);
                navigate("/forgot-email");
              }}
              aria-label={t("auth.login.forgotUsername")}
            >
              {t("auth.login.forgotUsername")}
            </Button>
            <Button
              variant="text"
              className="auth-action-button"
              onClick={() => {
                trackForgotPasswordEvent(ForgotPasswordEvent.CLICKED);
                navigate("/forgot-password");
              }}
              aria-label={t("auth.login.forgotPassword")}
            >
              {t("auth.login.forgotPassword")}
            </Button>
          </div>
          <Button
            variant="text"
            className="auth-action-button"
            onClick={onRegisterClick}
            aria-label={t("auth.login.register")}
          >
            {t("auth.login.register")}
          </Button>
          <div className="auth-action-button auth-faq-button">
            <span>{t("auth.login.faqPrompt")} </span>
            <a
              href="https://www.freedommortgage.com/frequently-asked-questions"
              target="_blank"
              rel="noopener noreferrer"
              className="auth-action-button underline decoration-solid"
            >
              {t("auth.login.faqLink")}
            </a>
          </div>
        </div>
      </div>
      {showUnauthorizedAcpBanner && !error && (
        <div
          ref={unauthorizedAcpRef}
          tabIndex={-1}
          className={cn(loginFormErrorStyles())}
          data-testid="acp-unauthorized-error"
        >
          <AcpUnauthorizedBanner />
        </div>
      )}
      {showSessionTimeoutAlert && !error && (
        <div ref={sessionTimeoutRef} className={cn(loginFormErrorStyles())} data-testid="session-timeout-error">
          <ErrorBanner
            message={"For your security, your session has expired. Log in again to continue."}
            testID="session-timeout-error-banner"
          />
        </div>
      )}
      {showEmailRecoveredSuccess && !error && (
        <div
          ref={emailRecoveredRef}
          tabIndex={-1}
          className={cn(loginFormErrorStyles())}
          data-testid="email-recovered-success"
        >
          <SuccessBanner
            message={t("auth.login.emailRecoveredSuccess")}
            testID="email-recovered-success-banner"
            data-testid="email-recovered-success-banner"
          />
        </div>
      )}
      {showPasswordResetSuccess && !error && (
        <div
          ref={passwordResetRef}
          tabIndex={-1}
          className={cn(loginFormErrorStyles())}
          data-testid="password-reset-success"
        >
          <SuccessBanner
            message={t("auth.login.passwordResetSuccess")}
            testID="password-reset-success-banner"
            data-testid="password-reset-success-banner"
          />
        </div>
      )}
      {error && (
        <div ref={errorRef} className={cn(loginFormErrorStyles())} data-testid="login-form-error">
          <ErrorBanner
            message={getErrorMessage()}
            testID="login-error"
            className="!py-2 !px-4"
          />
        </div>
      )}
    </div>
  );
}
